import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Skeleton } from '@lineageatlas/design-system';
import { useApi } from '../hooks/useApi';
import type { CellLine, LotStatus } from '@lineageatlas/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

type InventoryLot = CellLine;

interface LowStockLot {
  id: string;
  lot_number: string;
  cell_type: string;
  vials_remaining: number;
  storage_location: string | null;
}

type SortKey = 'vials_asc' | 'name' | 'date';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function vialColor(remaining: number, total: number): string {
  if (total === 0) return 'var(--color-text-muted)';
  const pct = remaining / total;
  if (pct > 0.5) return 'var(--color-success, #15803d)';
  if (pct >= 0.25) return 'var(--color-warning, #a16207)';
  return 'var(--color-danger, #b91c1c)';
}

function vialBarColor(remaining: number, total: number): string {
  if (total === 0) return 'var(--color-border)';
  const pct = remaining / total;
  if (pct > 0.5) return 'var(--color-success, #15803d)';
  if (pct >= 0.25) return 'var(--color-warning, #a16207)';
  return 'var(--color-danger, #b91c1c)';
}

function statusBadgeVariant(status: LotStatus): 'excellent' | 'good' | 'caution' | 'risk' {
  switch (status) {
    case 'released':
    case 'in_use':
      return 'excellent';
    case 'received':
    case 'in_qc':
      return 'good';
    case 'quarantined':
      return 'risk';
    default:
      return 'caution';
  }
}

const LOT_STATUS_LABELS: Record<LotStatus, string> = {
  received: 'Received',
  in_qc: 'In QC',
  released: 'Released',
  quarantined: 'Quarantined',
  in_use: 'In Use',
  depleted: 'Depleted',
  retired: 'Retired',
};

// ─── Responsive styles ────────────────────────────────────────────────────────

const styles = `
  .inv-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
  @media (min-width: 640px) {
    .inv-kpi-grid { grid-template-columns: repeat(3, 1fr) !important; }
  }
  .inv-table { display: none; }
  .inv-cards { display: grid; }
  @media (min-width: 768px) {
    .inv-table { display: table; }
    .inv-cards { display: none; }
  }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  loading,
  accent,
}: {
  label: string;
  value: string | number;
  loading?: boolean;
  accent?: string;
}) {
  return (
    <Card>
      <div
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--space-1)',
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      {loading ? (
        <Skeleton width="40%" height="28px" />
      ) : (
        <div
          style={{
            fontSize: 'var(--text-h2)',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            color: accent ?? 'var(--color-text)',
            letterSpacing: 'var(--tracking-heading)',
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
      )}
    </Card>
  );
}

function VialBar({ remaining, total }: { remaining: number; total: number }) {
  const pct = total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={remaining}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={`${remaining} of ${total} vials remaining`}
      style={{
        width: '100%',
        height: '6px',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-border)',
        overflow: 'hidden',
        marginTop: 'var(--space-1)',
      }}
    >
      <div
        style={{
          width: `${pct * 100}%`,
          height: '100%',
          background: vialBarColor(remaining, total),
          transition: 'width 0.3s ease',
        }}
      />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: '1 1 160px' }}
    >
      <span
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          fontWeight: 500,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: '36px',
          padding: '0 var(--space-8) 0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg-input, var(--color-bg-surface))',
          color: 'var(--color-text)',
          fontSize: 'var(--text-body)',
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          outline: 'none',
          appearance: 'none',
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right var(--space-3) center',
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function InventorySkeleton() {
  return (
    <div>
      <div
        className="inv-kpi-grid"
        style={{ display: 'grid', gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}
      >
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <Skeleton width="60%" height="12px" />
            <div style={{ marginTop: 'var(--space-2)' }}>
              <Skeleton width="40%" height="28px" />
            </div>
          </Card>
        ))}
      </div>
      <Card>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 'var(--space-4)',
              padding: 'var(--space-3) 0',
              borderBottom: i < 4 ? '1px solid var(--color-border)' : 'none',
            }}
          >
            <Skeleton width="20%" height="16px" />
            <Skeleton width="12%" height="16px" />
            <Skeleton width="18%" height="16px" />
            <Skeleton width="30%" height="16px" />
            <Skeleton width="12%" height="16px" />
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function InventoryPage() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('vials_asc');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const {
    data: inventory,
    loading: invLoading,
    error: invError,
  } = useApi<InventoryLot[]>('/api/v1/inventory');
  const { data: lowStockData, loading: lowStockLoading } = useApi<LowStockLot[]>(
    '/api/v1/inventory/low-stock',
  );

  const loading = invLoading || lowStockLoading;

  const totalVials = useMemo(
    () => (inventory ?? []).reduce((sum, l) => sum + (l.vials_remaining ?? 0), 0),
    [inventory],
  );

  const lowStockCount = useMemo(
    () =>
      (inventory ?? []).filter((l) => (l.vials_remaining ?? 0) <= 5 && (l.vials_remaining ?? 0) > 0)
        .length,
    [inventory],
  );

  const emptyCount = useMemo(
    () => (inventory ?? []).filter((l) => (l.vials_remaining ?? 0) === 0).length,
    [inventory],
  );

  const filteredAndSorted = useMemo(() => {
    let result = inventory ?? [];

    if (statusFilter !== 'all') result = result.filter((l) => l.status === statusFilter);
    if (lowStockOnly) result = result.filter((l) => (l.vials_remaining ?? 0) <= 5);

    return [...result].sort((a, b) => {
      if (sortKey === 'vials_asc') return (a.vials_remaining ?? 0) - (b.vials_remaining ?? 0);
      if (sortKey === 'name') return (a.lot_number ?? '').localeCompare(b.lot_number ?? '');
      if (sortKey === 'date')
        return new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime();
      return 0;
    });
  }, [inventory, statusFilter, sortKey, lowStockOnly]);

  const alertLots = lowStockData ?? [];

  if (loading)
    return (
      <>
        <style>{styles}</style>
        <InventorySkeleton />
      </>
    );

  if (invError) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: 'var(--space-16)',
          color: 'var(--color-text-muted)',
        }}
      >
        <p
          style={{
            fontSize: 'var(--text-h3)',
            fontFamily: 'var(--font-display)',
            marginBottom: 'var(--space-2)',
          }}
        >
          Unable to load inventory
        </p>
        <p style={{ fontSize: 'var(--text-caption)' }}>
          Check that the API server is running on port 8787.
        </p>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>

      <div>
        {/* Page header */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              letterSpacing: 'var(--tracking-heading)',
              lineHeight: 'var(--leading-heading)',
              color: 'var(--color-text)',
              margin: '0 0 var(--space-2)',
            }}
          >
            Inventory
          </h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-body)', margin: 0 }}>
            Vial counts, storage locations, and low-stock alerts across all lots.
          </p>
        </div>

        {/* KPI row */}
        <div
          className="inv-kpi-grid"
          style={{ display: 'grid', gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}
        >
          <KpiCard label="Total Vials" value={totalVials} />
          <KpiCard
            label="Low Stock Lots"
            value={lowStockCount}
            accent={lowStockCount > 0 ? 'var(--color-warning, #a16207)' : undefined}
          />
          <KpiCard
            label="Empty Lots"
            value={emptyCount}
            accent={emptyCount > 0 ? 'var(--color-danger, #b91c1c)' : undefined}
          />
        </div>

        {/* Low-stock alerts */}
        {alertLots.length > 0 && (
          <div style={{ marginBottom: 'var(--space-8)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-h3)',
                fontWeight: 600,
                letterSpacing: 'var(--tracking-heading)',
                color: 'var(--color-text)',
                margin: '0 0 var(--space-4)',
              }}
            >
              Low Stock Alerts
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 'var(--space-3)',
              }}
            >
              {alertLots.map((lot) => (
                <Card key={lot.id}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-body)',
                        fontWeight: 600,
                        color: 'var(--color-text)',
                      }}
                    >
                      {lot.lot_number}
                    </span>
                    <Badge status="risk">
                      {lot.vials_remaining} vial{lot.vials_remaining !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div
                    style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-muted)' }}
                  >
                    {lot.cell_type}
                    {lot.storage_location && (
                      <span style={{ marginLeft: 'var(--space-2)' }}>· {lot.storage_location}</span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-6)',
            alignItems: 'flex-end',
          }}
        >
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'received', label: 'Received' },
              { value: 'in_qc', label: 'In QC' },
              { value: 'released', label: 'Released' },
              { value: 'quarantined', label: 'Quarantined' },
              { value: 'in_use', label: 'In Use' },
              { value: 'depleted', label: 'Depleted' },
              { value: 'retired', label: 'Retired' },
            ]}
          />
          <FilterSelect
            label="Sort by"
            value={sortKey}
            onChange={(v) => setSortKey(v as SortKey)}
            options={[
              { value: 'vials_asc', label: 'Vials remaining (low first)' },
              { value: 'name', label: 'Lot name (A–Z)' },
              { value: 'date', label: 'Date received (newest)' },
            ]}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              cursor: 'pointer',
              fontSize: 'var(--text-body)',
              color: 'var(--color-text)',
              paddingBottom: 'var(--space-1)',
            }}
          >
            <input
              type="checkbox"
              checked={lowStockOnly}
              onChange={(e) => setLowStockOnly(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              aria-label="Show low stock only"
            />
            Low stock only
          </label>
        </div>

        {/* Table — desktop */}
        {filteredAndSorted.length === 0 ? (
          <Card>
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--text-body)',
                margin: 0,
                textAlign: 'center',
                padding: 'var(--space-8) 0',
              }}
            >
              No lots match the selected filters.
            </p>
          </Card>
        ) : (
          <>
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <table
                className="inv-table"
                style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
                aria-label="Inventory table"
              >
                <thead>
                  <tr
                    style={{
                      background: 'var(--color-bg-subtle, var(--color-bg-surface))',
                      borderBottom: '2px solid var(--color-border)',
                    }}
                  >
                    {(
                      ['Lot Name', 'Cell Type', 'Storage Location', 'Vials', 'Status'] as const
                    ).map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: 'var(--space-3) var(--space-4)',
                          textAlign: 'left',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 600,
                          color: 'var(--color-text-muted)',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.map((lot, idx) => {
                    const total = lot.vial_count ?? 0;
                    const remaining = lot.vials_remaining ?? 0;
                    return (
                      <tr
                        key={lot.id}
                        style={{
                          borderBottom:
                            idx < filteredAndSorted.length - 1
                              ? '1px solid var(--color-border)'
                              : 'none',
                        }}
                      >
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <Link
                            to={`/lots/${lot.id}`}
                            style={{
                              color: 'var(--color-accent-primary, #6d28d9)',
                              fontWeight: 600,
                              fontSize: 'var(--text-body)',
                              textDecoration: 'none',
                            }}
                          >
                            {lot.lot_number}
                          </Link>
                          <div
                            style={{
                              fontSize: 'var(--text-xs)',
                              color: 'var(--color-text-muted)',
                              marginTop: 'var(--space-1)',
                              fontFamily: 'var(--font-mono)',
                            }}
                          >
                            {lot.cell_type}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-body)',
                            color: 'var(--color-text)',
                          }}
                        >
                          {lot.cell_type}
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-body)',
                            color: lot.storage_location
                              ? 'var(--color-text)'
                              : 'var(--color-text-muted)',
                          }}
                        >
                          {lot.storage_location ?? '—'}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontFeatureSettings: '"tnum"',
                              fontSize: 'var(--text-body)',
                              fontWeight: 600,
                              color: vialColor(remaining, total),
                            }}
                          >
                            {remaining}/{total}
                          </span>
                          <VialBar remaining={remaining} total={total} />
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <Badge status={statusBadgeVariant(lot.status)}>
                            {LOT_STATUS_LABELS[lot.status]}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="inv-cards" style={{ gridTemplateColumns: '1fr', gap: 0 }}>
                {filteredAndSorted.map((lot, idx) => {
                  const total = lot.vial_count ?? 0;
                  const remaining = lot.vials_remaining ?? 0;
                  return (
                    <div
                      key={lot.id}
                      style={{
                        padding: 'var(--space-4)',
                        borderBottom:
                          idx < filteredAndSorted.length - 1
                            ? '1px solid var(--color-border)'
                            : 'none',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 'var(--space-2)',
                          marginBottom: 'var(--space-2)',
                        }}
                      >
                        <Link
                          to={`/lots/${lot.id}`}
                          style={{
                            color: 'var(--color-accent-primary, #6d28d9)',
                            fontWeight: 600,
                            fontSize: 'var(--text-body)',
                            textDecoration: 'none',
                          }}
                        >
                          {lot.lot_number}
                        </Link>
                        <Badge status={statusBadgeVariant(lot.status)}>
                          {LOT_STATUS_LABELS[lot.status]}
                        </Badge>
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--text-caption)',
                          color: 'var(--color-text-muted)',
                          marginBottom: 'var(--space-2)',
                        }}
                      >
                        {lot.cell_type}
                        {lot.storage_location && (
                          <span style={{ marginLeft: 'var(--space-2)' }}>
                            · {lot.storage_location}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontFeatureSettings: '"tnum"',
                            fontSize: 'var(--text-body)',
                            fontWeight: 600,
                            color: vialColor(remaining, total),
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {remaining}/{total} vials
                        </span>
                        <div style={{ flex: 1 }}>
                          <VialBar remaining={remaining} total={total} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
